import React from 'react';
import {
  Bug,
  CreditCard,
  Lightbulb,
  UserX,
  MessageCircle,
  CircleHelp,
  PlusCircle,
  ArrowLeftRight,
  AlertTriangle,
  Link2,
  RefreshCw,
  MessageSquareText,
  Circle,
} from 'lucide-react';

/**
 * Format an ISO date string into a human-readable date/time string.
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export interface BadgeStyle {
  bg: string;
  border: string;
  color: string;
}

/**
 * Get status badge styles for a ticket status. Requires isDark from parent theme context.
 */
export const getStatusBadge = (status: string, isDark: boolean): BadgeStyle => {
  switch (status.toLowerCase()) {
    case 'open':
      return {
        bg: isDark ? '#1C398E' : '#EFF6FF',
        border: isDark ? '#155DFC' : '#8EC5FF',
        color: isDark ? '#8EC5FF' : '#155DFC',
      };
    case 'in_progress':
      return {
        bg: isDark ? '#78350F' : '#FFFBEB',
        border: isDark ? '#F59E0B' : '#FFD230',
        color: isDark ? '#FCD34D' : '#E17100',
      };
    case 'waiting_for_customer':
      return {
        bg: isDark ? '#7C2D12' : '#FFF7ED',
        border: isDark ? '#F97316' : '#FDBA74',
        color: isDark ? '#FDBA74' : '#C2410C',
      };
    case 'resolved':
      return {
        bg: isDark ? '#064E3B' : '#ECFDF5',
        border: isDark ? '#10B981' : '#5EE9B5',
        color: isDark ? '#6EE7B7' : '#009966',
      };
    case 'closed':
      return {
        bg: isDark ? '#1D293D' : '#F8FAFC',
        border: isDark ? '#314158' : '#CBD5E1',
        color: isDark ? '#90A1B9' : '#62748E',
      };
    default:
      return {
        bg: isDark ? '#1D293D' : '#F8FAFC',
        border: isDark ? '#314158' : '#CBD5E1',
        color: isDark ? '#90A1B9' : '#62748E',
      };
  }
};

/**
 * Get priority badge styles for a ticket priority. Requires isDark from parent theme context.
 */
export const getPriorityBadge = (priority: string, isDark: boolean): BadgeStyle => {
  switch (priority.toLowerCase()) {
    case 'urgent':
      return {
        bg: isDark ? '#4C0519' : '#FFF1F2',
        border: isDark ? '#FB7185' : '#FFA1AD',
        color: isDark ? '#FDA4AF' : '#EC003F',
      };
    case 'high':
      return {
        bg: isDark ? '#7C2D12' : '#FFF7ED',
        border: isDark ? '#F97316' : '#FDBA74',
        color: isDark ? '#FDBA74' : '#C2410C',
      };
    case 'medium':
      return {
        bg: isDark ? '#78350F' : '#FFFBEB',
        border: isDark ? '#F59E0B' : '#FFD230',
        color: isDark ? '#FCD34D' : '#E17100',
      };
    case 'low':
      return {
        bg: isDark ? '#064E3B' : '#ECFDF5',
        border: isDark ? '#10B981' : '#5EE9B5',
        color: isDark ? '#6EE7B7' : '#009966',
      };
    default:
      return {
        bg: isDark ? '#1D293D' : '#F8FAFC',
        border: isDark ? '#314158' : '#CBD5E1',
        color: isDark ? '#90A1B9' : '#62748E',
      };
  }
};

/**
 * Return the icon element for a ticket category.
 */
export const getCategoryIcon = (category: string): React.ReactElement => {
  switch (category) {
    case 'technical_issue':
      return <Bug size={14} />;
    case 'billing':
      return <CreditCard size={14} />;
    case 'feature_request':
      return <Lightbulb size={14} />;
    case 'account':
      return <UserX size={14} />;
    case 'bug_report':
      return <Bug size={14} />;
    case 'general_inquiry':
      return <MessageCircle size={14} />;
    default:
      return <CircleHelp size={14} />;
  }
};

/**
 * Return the icon element for a ticket history event type.
 */
export const getEventIcon = (eventType: string): React.ReactElement => {
  switch (eventType) {
    case 'created':
      return <PlusCircle size={14} color="#FFFFFF" />;
    case 'status_changed':
      return <ArrowLeftRight size={14} color="#FFFFFF" />;
    case 'priority_changed':
      return <AlertTriangle size={14} color="#FFFFFF" />;
    case 'jira_created':
      return <Link2 size={14} color="#FFFFFF" />;
    case 'jira_updated':
      return <RefreshCw size={14} color="#FFFFFF" />;
    case 'comment_added':
      return <MessageSquareText size={14} color="#FFFFFF" />;
    default:
      return <Circle size={14} color="#FFFFFF" />;
  }
};

/**
 * Return the accent color for a ticket history event type.
 */
export const getEventColor = (eventType: string): string => {
  switch (eventType) {
    case 'created':
      return '#3B82F6';
    case 'status_changed':
      return '#8B5CF6';
    case 'priority_changed':
      return '#F97316';
    case 'jira_created':
    case 'jira_updated':
      return '#10B981';
    case 'comment_added':
      return '#6366F1';
    default:
      return '#9CA3AF';
  }
};

/**
 * Flatten help data into an array of articles augmented with categoryTitle and categoryIcon,
 * optionally filtered by a case-insensitive search query against article title/content and
 * category title.
 *
 * Replaces the two nearly-identical local helpers getFilteredArticles() and
 * getTicketSearchArticles() in Support.tsx.
 *
 * Signature: filterArticles(helpData, query)
 *   - helpData: the imported help JSON ({ categories: [{ title, icon, articles: [...] }] })
 *   - query: the raw search string (may be empty/whitespace); when blank, returns all
 *     articles across all categories.
 */
export interface HelpArticleStep {
  title?: string;
  content?: string;
  [key: string]: unknown;
}

export interface HelpArticle {
  id: string;
  title: string;
  content: string;
  steps: HelpArticleStep[];
  [key: string]: unknown;
}

export interface HelpCategory {
  title: string;
  icon?: string;
  articles: HelpArticle[];
}

export interface HelpData {
  categories: HelpCategory[];
}

export interface FilteredArticle extends HelpArticle {
  categoryTitle: string;
  categoryIcon?: string;
}

export const filterArticles = (helpData: HelpData, query: string): FilteredArticle[] => {
  const trimmed = (query || '').trim();
  if (!trimmed) {
    return helpData.categories.flatMap((category) =>
      category.articles.map((article) => ({
        ...article,
        categoryTitle: category.title,
        categoryIcon: category.icon,
      }))
    );
  }

  const q = trimmed.toLowerCase();
  return helpData.categories.flatMap((category) =>
    category.articles
      .filter((article) =>
        article.title.toLowerCase().includes(q) ||
        article.content.toLowerCase().includes(q) ||
        category.title.toLowerCase().includes(q)
      )
      .map((article) => ({
        ...article,
        categoryTitle: category.title,
        categoryIcon: category.icon,
      }))
  );
};
